import type { MessageEventData, WorkerResponse } from "@/workers/parseWorker"
import yaml from "js-yaml"
import { DUMMY_UUID } from "@/utils"
import { useEngineModel } from "@/utils/useEngineModel"
import workerImport from "@/workers/parseWorker?worker&url"

export interface Test {
  id: string
  name?: string
  note?: string
  formula: string
  contextId?: string
  expected: string
}

// Transient UI/run state for a test execution
export interface TestResult {
  actual?: string
  pass?: boolean
  error?: string
  running?: boolean
}

export function useTestHarnessRunner() {
  const model = useEngineModel()

  // Parse YAML string to extract tests array
  function parseYamlTests(yamlString: string) {
    try {
      const doc = yaml.load(yamlString)
      if (typeof doc === "object" && doc && "tests" in doc) {
        return doc.tests as Test[]
      } else {
        throw new Error("Invalid YAML format: 'tests' key not found")
      }
    } catch (error) {
      console.error(error)
    }

    return [] as Test[]
  }

  // Export tests array to YAML string
  function exportYamlTests(tests: any[]) {
    return yaml.dump({ tests })
  }

  // Run a single formula and return the output table
  function parseCSVWithWorker(data: string, childData: string | string[]) {
    // Use the parseWorker to process the CSV and childData
    return new Promise((resolve, reject) => {
      const worker = new Worker(workerImport, { type: "module" })
      worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
        const payload = e.data
        if (!payload.ok) {
          reject(new Error(payload.error.message))
        } else {
          resolve(payload.data)
        }
        worker.terminate()
      }
      worker.onerror = (e) => {
        reject(e)
        worker.terminate()
      }
      const messageData: MessageEventData = {
        type: "parseAndSort",
        childData,
        data
      }
      worker.postMessage(messageData)
    })
  }

  async function runTest(test: Test): Promise<TestResult> {
    const result: TestResult = { actual: "", pass: false, error: undefined }

    try {
      const { data, childData } = await model.api.playground(test.contextId || DUMMY_UUID, test.formula)

      const table: string[][] | undefined = (await parseCSVWithWorker(data, childData)) as string[][]

      const prettified = table.map((row) => row.join(",  ")).join("\n")

      result.actual = prettified

      result.pass = result.actual === test.expected
    } catch (e: any) {
      console.error("Error running test:", e)
      result.pass = false
      result.error = e?.message ?? String(e)
    }

    return result
  }

  return { runTest, parseYamlTests, exportYamlTests }
}
